import type { Booking, BookingGarment } from '../api/bookings';

/**
 * What the alterations on a booking cost, split by who pays.
 *
 * <p>
 * <strong>Absorbed is not free, and that distinction is the whole reason this exists.</strong> Many
 * shops in this trade alter at no charge and advertise it — it is often why the customer picked
 * them. But the tailor is paid either way. If "free" were modelled by setting the charge to zero,
 * the shop would lose the cost side of every garment it ever altered, and §20's per-unit ROI would
 * report a profit that never happened.
 * </p>
 *
 * <p>
 * So the charge is always recorded and a flag decides whether it reaches the invoice. Billed money
 * is revenue and taxable; absorbed money is a cost against the unit and appears on no customer
 * document at all — except, deliberately, as the sentence saying it was not charged.
 * </p>
 */
export interface AlterationTotals {
  /** Charged to the customer, and therefore part of the invoice and its tax. */
  billed: number;
  /** Paid by the shop. A cost against the garments, never on the invoice. */
  absorbed: number;
  /** Every alteration on the booking, however it was paid for. */
  total: number;
  /** Whether anything was done at the shop's expense — what the "included free" line keys on. */
  hasAbsorbed: boolean;
}

function garmentsOf(booking: Booking): BookingGarment[] {
  return booking.wearers.flatMap((wearer) => wearer.garments);
}

export function alterationTotals(booking: Booking): AlterationTotals {
  let billed = 0;
  let absorbed = 0;

  for (const garment of garmentsOf(booking)) {
    const charge = garment.alterationCharge ?? 0;

    // A garment with no alteration work contributes nothing either way — a zero charge on a
    // billable garment is not the same as an absorbed one, and only the latter earns the notice.
    if (charge <= 0 && garment.alterations.length === 0) {
      continue;
    }

    if (garment.alterationBillable) {
      billed += charge;
    } else {
      absorbed += charge;
    }
  }

  return {
    billed,
    absorbed,
    total: billed + absorbed,
    // True even when the absorbed work happened to cost nothing: what the customer is being told is
    // that their alterations were not charged for, which is worth saying regardless of the figure.
    hasAbsorbed: garmentsOf(booking).some(
      (garment) => !garment.alterationBillable && garment.alterations.length > 0,
    ),
  };
}

/** One named charge, ready to render as a money line on either surface. */
export interface RecoveryLine {
  key: 'lateFee' | 'damage' | 'accessoryReplacement' | 'cancellationFee';
  label: string;
  amount: number;
}

/**
 * The charges assessed at return, named — in the order a settlement makes them.
 *
 * <p>
 * <strong>One definition for two surfaces.</strong> The booking's money panel and the printed
 * customer copy both showed charge lines that did not add up to their own total once a booking was
 * settled: a late fee, damage or a missing accessory went into `invoiceTotal` with nothing naming
 * it, so a booking charged ₹6,000 for ten late days read "Rental ₹4,500 · GST ₹1,260 · Invoice
 * total ₹11,760" and the counter had no answer for the gap. Two copies of the wording would drift,
 * which is the argument `MoneyLine` and `tones.ts` already carry.
 * </p>
 *
 * <p>
 * <strong>Three lines, never one "charged on return".</strong> Days kept, a mark on the cloth and a
 * kalgi that did not come back are three different conversations, and a combined figure answers
 * none of them — the same reason the two discount levels are printed apart.
 * </p>
 *
 * <p>
 * A kind that was not charged is absent rather than zero, so nothing renders for it. The server
 * sends null for exactly that reason.
 * </p>
 */
export function recoveryLines(booking: Booking): RecoveryLine[] {
  const { lateFee, damage, accessoryReplacement, cancellationFee } = booking.recoveries;

  const lines: RecoveryLine[] = [];

  if (lateFee) {
    lines.push({ key: 'lateFee', label: 'Late return', amount: lateFee });
  }

  if (damage) {
    lines.push({ key: 'damage', label: 'Damage', amount: damage });
  }

  if (accessoryReplacement) {
    lines.push({
      key: 'accessoryReplacement',
      label: 'Accessories not returned',
      amount: accessoryReplacement,
    });
  }

  if (cancellationFee) {
    lines.push({ key: 'cancellationFee', label: 'Cancellation charge', amount: cancellationFee });
  }

  return lines;
}
